import React, { useState, useEffect, useRef } from "react";
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  collection,
  where,
} from "firebase/firestore";
import useUserStore from "../../zustand-stores/UserStore";
import { FIREBASE_CONVERSATIONS } from "../../Utils/constant";
import { db } from "../../Utils/firebase";
import "../../css/style.css";
import DOMPurify from "dompurify";
import MyEditor from "../AddNewTaskModalEditor";
import {
  useGetTribeGroups,
  useCreateMultiGroupPost,
} from "../../Controllers/Tribe/TribePostController";
import useAuthStore from "../../zustand-stores/AuthStore";
import { toast } from "react-toastify";

const ShareLevelupModal = () => {
  const [showModal, setShowModal] = useState(false);
  const [levelUpImageUrl, setLevelUpImageUrl] = useState(null);
  const [uploadedFileData, setUploadedFileData] = useState(null);
  const [rawBlobFile, setRawBlobFile] = useState(null);
  const [levelData, setLevelData] = useState(null);
  const [selectedChats, setSelectedChats] = useState([]);
  const [description, setDescription] = useState("");
  const [isSharable, setIsSharable] = useState(true);
  const [selectedConversationIds, setSelectedConversationIds] = useState([]);
  const [selectedTribeGroupIds, setSelectedTribeGroupIds] = useState([]);

  // Combined list of users and groups
  const [allConversations, setAllConversations] = useState([]);
  const [isChatDropdownOpen, setIsChatDropdownOpen] = useState(false);
  const [isTribeGroupDropdownOpen, setIsTribeGroupDropdownOpen] =
    useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingAllUsers, setLoadingAllUsers] = useState(false);

  const [loadingFromFirestore, setLoadingFromFirestore] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const { authSessionExists } = useAuthStore();

  const {
    data: tribeGroups = [],
    isLoading: tribeGroupsLoading,
    error: tribeGroupsError,
  } = useGetTribeGroups("", "All", authSessionExists);
  const { mutate: createMultiGroupPost, isPending: isPostingToTribe } =
    useCreateMultiGroupPost();

  const { user } = useUserStore();
  const chatDropdownRef = useRef(null);
  const tribeGroupDropdownRef = useRef(null);

  const prepareConversationList = async (userId) => {
    setLoadingFromFirestore(true);
    try {
      const q = query(
        collection(db, FIREBASE_CONVERSATIONS),
        where("participants", "array-contains", String(userId)),
        orderBy("lastMessageTime", "desc"),
      );
      const querySnapshot = await getDocs(q);

      const allConversationsList = [];

      querySnapshot.forEach((doc) => {
        const conversationData = doc.data();
        const conversationId = doc.id;

        if (conversationData.type === "direct") {
          // For direct chats, find the other participant
          const otherParticipantId = conversationData.participants.find(
            (participantId) => participantId !== String(userId),
          );

          if (
            otherParticipantId &&
            conversationData.participantDetails &&
            conversationData.participantDetails[otherParticipantId]
          ) {
            const otherUser =
              conversationData.participantDetails[otherParticipantId];
            allConversationsList.push({
              conversationId: conversationId,
              name: otherUser.name,
              email: otherUser.email,
              profileImage: otherUser.profileImage,
              type: "direct",
              displayText: `${otherUser.name}`,
            });
          }
        } else if (conversationData.type === "group") {
          // For group chats, use group name or create from participants
          const groupName = `${conversationData?.metadata?.groupName} (${conversationData.participants.length} members)`;

          allConversationsList.push({
            conversationId: conversationId,
            name: groupName,
            memberCount: conversationData.participants.length,
            type: "group",
            displayText: `${groupName}`,
          });
        }
      });

      setAllConversations(allConversationsList);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
    setLoadingFromFirestore(false);
  };

  const searchUsers = async (term) => {
    if (!term || term.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchingUsers(true);

    try {
      const searchTerm = term.toLowerCase().trim();

      // Filter all users client-side (case-insensitive) to support substring matching
      const results = allUsers
        .filter((u) => u.name?.toLowerCase().includes(searchTerm))
        .slice(0, 10);

      setSearchResults(results);
    } catch (err) {
      console.error("Error searching users:", err);
      setSearchResults([]);
    }

    setSearchingUsers(false);
  };

  // Ensure a direct conversation exists for two users and return conversationId
  const ensureDirectConversationWith = async (otherUser) => {
    try {
      const userId = String(user.id);
      const otherUserId = String(otherUser.id);
      const sortedIds = [userId, otherUserId].sort(
        (a, b) => Number(a) - Number(b),
      );
      const conversationId = `${sortedIds[0]}_${sortedIds[1]}`;

      const convoRef = doc(db, FIREBASE_CONVERSATIONS, conversationId);
      const convoSnap = await getDoc(convoRef);

      const participants = [userId, otherUserId];
      const participantDetails = {
        [userId]: {
          name: user.full_name || user.name || "",
          email: user.email || "",
          profileImage: user.image || "",
        },
        [otherUserId]: {
          name: otherUser.name || otherUser.full_name || "",
          email: otherUser.email || "",
          profileImage: otherUser.profileImage || otherUser.image || "",
        },
      };

      if (!convoSnap.exists()) {
        await setDoc(convoRef, {
          conversationId,
          participants,
          participantDetails,
          lastMessage: "",
          lastMessageSenderId: null,
          lastMessageTime: serverTimestamp(),
          unreadCount: { [userId]: 0, [otherUserId]: 0 },
          type: "direct",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isActive: true,
          visibleTo: participants,
        });
      }

      return conversationId;
    } catch (err) {
      console.error("Error ensuring conversation:", err);
      return null;
    }
  };

  // Debounced search trigger
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchQuery && searchQuery.trim().length >= 2) {
        searchUsers(searchQuery.trim());
      } else {
        setSearchResults([]);
      }
    }, 350);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    const handleShareLevelUp = (event) => {
      const { uploadedFile, levelData, rawBlobFile } = event.detail;

      // Set the uploaded file data and extract the image URL
      setUploadedFileData(uploadedFile);
      setRawBlobFile(rawBlobFile);
      setLevelData(levelData);
      setLevelUpImageUrl(uploadedFile?.media_url || uploadedFile?.url);
      setShowModal(true);
    };

    window.addEventListener("shareLevelUp", handleShareLevelUp);
    return () => window.removeEventListener("shareLevelUp", handleShareLevelUp);
  }, []);

  useEffect(() => {
    if (user && user.id) {
      prepareConversationList(user.id);
    }
  }, [user]);

  // Fetch All Users for client-side searching
  useEffect(() => {
    if (!showModal || !user?.id) return;

    const fetchAllUsers = async () => {
      setLoadingAllUsers(true);
      try {
        const usersRef = collection(db, "users");
        const q = query(usersRef);
        const snapshot = await getDocs(q);

        const users = [];
        snapshot.forEach((doc) => {
          if (String(doc.id) !== String(user.id)) {
            users.push({ id: doc.id, ...doc.data() });
          }
        });

        setAllUsers(users);
      } catch (err) {
        console.error("Error fetching all users:", err);
        setAllUsers([]);
      }
      setLoadingAllUsers(false);
    };

    fetchAllUsers();
  }, [showModal, user?.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        chatDropdownRef.current &&
        !chatDropdownRef.current.contains(event.target)
      ) {
        setIsChatDropdownOpen(false);
      }
      if (
        tribeGroupDropdownRef.current &&
        !tribeGroupDropdownRef.current.contains(event.target)
      ) {
        setIsTribeGroupDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const backdropStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0, 0, 0, 0.5)", // Standard modal backdrop dimming
    zIndex: 1050,
  };

  const dialogStyle = {
    // Full control over centering using flexbox on the main container
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1055,
  };

  const contentStyle = {
    // This is your outer modal box, matching the background color #132534
    background: "#132534",
    borderRadius: "24px",
    overflow: "hidden",
    boxShadow: "0 0 50px rgba(0, 0, 0, 0.5)",

    maxWidth: "650px",
    width: "90%",
    margin: "1.75rem", // Added margin for spacing on small screens
    position: "relative", // Necessary for absolute positioning of the close button
    padding: "32px",
  };

  const handleClose = () => {
    setShowModal(false);
    setLevelUpImageUrl(null);
    setUploadedFileData(null);
    setLevelData(null);
    setSelectedChats([]);
    setDescription("");
    setIsSharable(true);
    setSelectedConversationIds([]);
    setSelectedTribeGroupIds([]);
    setIsChatDropdownOpen(false);
    setIsTribeGroupDropdownOpen(false);
    setIsSharing(false);
  };

  const handleChatSelection = (conversationId) => {
    const isSelected = selectedChats.includes(conversationId);
    let newSelectedChats;

    if (isSelected) {
      // Remove from selection
      newSelectedChats = selectedChats.filter((id) => id !== conversationId);
    } else {
      // Add to selection
      newSelectedChats = [...selectedChats, conversationId];
    }

    setSelectedChats(newSelectedChats);
    setSelectedConversationIds(newSelectedChats);
  };

  const getSelectedDisplayText = () => {
    if (selectedChats.length === 0) {
      return loadingFromFirestore ? "Loading chats..." : "Select Chats";
    }

    if (selectedChats.length === 1) {
      const selected = allConversations.find(
        (conv) => conv.conversationId === selectedChats[0],
      );
      return selected ? selected.displayText : "Unknown";
    }

    return `${selectedChats.length} chats selected`;
  };

  const handleTribeGroupSelection = (groupId) => {
    const isSelected = selectedTribeGroupIds.includes(groupId);
    let newSelectedGroups;

    if (isSelected) {
      // Remove from selection
      newSelectedGroups = selectedTribeGroupIds.filter((id) => id !== groupId);
    } else {
      // Add to selection
      newSelectedGroups = [...selectedTribeGroupIds, groupId];
    }

    setSelectedTribeGroupIds(newSelectedGroups);
  };

  const getSelectedTribeGroupsDisplayText = () => {
    if (selectedTribeGroupIds.length === 0) {
      return tribeGroupsLoading ? "Loading groups..." : "Select Tribe Groups";
    }

    if (selectedTribeGroupIds.length === 1) {
      const selected = tribeGroups.find(
        (group) => group.id === selectedTribeGroupIds[0],
      );
      return selected ? selected.name : "Unknown";
    }

    return `${selectedTribeGroupIds.length} groups selected`;
  };

  const handleDescriptionChange = (value) => {
    setDescription(value);
  };

  const handleShareToChat = async () => {
    if (
      selectedConversationIds.length === 0 &&
      selectedTribeGroupIds.length === 0
    ) {
      toast.warning(
        "⚠️ Please select at least one chat or tribe group to share",
      );
      return;
    }

    if (!uploadedFileData) {
      toast.warning("⚠️ No image data available for sharing");
      return;
    }

    setIsSharing(true);

    try {
      // Share to Firebase Chats
      if (selectedConversationIds.length > 0) {
        for (const conversationId of selectedConversationIds) {
          // Get conversation data to extract participants
          const convoRef = doc(db, FIREBASE_CONVERSATIONS, conversationId);
          const convoSnap = await getDoc(convoRef);

          if (!convoSnap.exists()) {
            console.error(`❌ Conversation ${conversationId} not found`);
            continue;
          }

          const convoData = convoSnap.data();
          const visibleToUserIds = convoData.participants || [];

          // Create message data structure (same as Chat.jsx)
          const messageData = {
            type: "text",
            metadata: {},
            reactions: {},
            isRead: null,
            senderId: user.id,
            senderName: user.full_name,
            senderProfileImage: user.image,
            timestamp: serverTimestamp(),
            content: description || "",
            isFile: true,
            visibleTo: visibleToUserIds,
          };

          // Add file data (the level-up screenshot)
          messageData.file = {
            thumbnail: uploadedFileData.media_url || uploadedFileData.url,
            url: uploadedFileData.media_url || uploadedFileData.url,
            type: uploadedFileData.type || "image/png",
          };

          // Add message to conversation
          await addDoc(
            collection(db, FIREBASE_CONVERSATIONS, conversationId, "messages"),
            messageData,
          );

          // Update conversation metadata
          await runTransaction(db, async (transaction) => {
            const convoSnap = await transaction.get(convoRef);
            if (!convoSnap.exists()) return;

            const convoData = convoSnap.data();
            const currentUnread = convoData.unreadCount || {};
            // Fix metadata duplication and sync participants/visibleTo
            const allParticipants = Array.from(
              new Set([
                ...(convoData.visibleTo || []),
                ...(convoData.participants || []),
              ]),
            ).filter(Boolean);

            const updatedUnread = { ...currentUnread };

            // Ensure every participant is initialized and incremented if not sender
            allParticipants.forEach((uid) => {
              const uidStr = String(uid);
              if (!(uidStr in updatedUnread)) {
                updatedUnread[uidStr] = 0;
              }

              if (uidStr !== String(user.id)) {
                updatedUnread[uidStr] = (updatedUnread[uidStr] || 0) + 1;
              }
            });

            const unreadUserIds = Object.keys(updatedUnread).filter(
              (uid) => updatedUnread[uid] > 0,
            );

            transaction.update(convoRef, {
              lastMessage: description || "Shared a level-up achievement",
              lastMessageSenderId: user.id,
              lastMessageTime: serverTimestamp(),
              updatedAt: serverTimestamp(),
              unreadCount: updatedUnread,
              unreadUserIds,
              participants: allParticipants, // Repair participants list
            });
          });
        }
      }

      // Share to Tribe Groups
      if (selectedTribeGroupIds.length > 0) {
        try {
          // Convert the uploaded image URL to a File object
          const imageFile = rawBlobFile;

          // Prepare FormData for multi-group post
          const formData = new FormData();

          // Add group IDs
          selectedTribeGroupIds.forEach((groupId) => {
            formData.append("group_ids[]", groupId);
          });

          // Add text content (description)
          formData.append(
            "text",
            description ||
              `🎉 Level Up! I've reached Level ${levelData.level}!`,
          );

          // Add the image file
          formData.append("media[]", imageFile);

          // Log FormData contents

          for (let [key, value] of formData.entries()) {
          }

          // Call the API to create posts in multiple groups
          createMultiGroupPost(formData, {
            onSuccess: (response) => {
              toast.success(
                `Level-up shared to ${selectedTribeGroupIds.length} tribe group(s)!`,
              );
            },
            onError: (error) => {
              console.error("❌ Error sharing to tribe groups:", error);
              toast.error("Failed to share to some tribe groups");
            },
          });
        } catch (error) {
          console.error("❌ Error preparing tribe post:", error);
          toast.error("Failed to share to tribe groups");
        }
      }

      // Show success message
      const totalShared =
        selectedConversationIds.length + selectedTribeGroupIds.length;
      toast.success(`Level-up shared to ${totalShared} destination(s)!`);

      handleClose();
    } catch (error) {
      console.error("❌ Error sharing level-up:", error);
      toast.error("Failed to share level-up. Please try again.");
    } finally {
      setIsSharing(false);
    }
  };

  if (!showModal) {
    return null;
  }

  return (
    <>
      <div style={backdropStyle} onClick={handleClose} />

      <div style={dialogStyle} role="dialog" aria-modal="true">
        <div style={contentStyle}>
          <button
            type="button"
            style={{
              position: "absolute",
              top: "10px",
              right: "15px",
              zIndex: 1060,
              background: "none",
              border: "none",
              color: "white",
              opacity: "1",
              fontSize: "24px",
              // fontWeight: 'bold',
              cursor: "pointer",
              textShadow: "0 0 5px rgba(0, 0, 0, 0.8)",
            }}
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
          {/* Modal Content */}
          <div style={{ padding: "0" }}>
            {/* Header */}
            <h2
              style={{
                color: "#E6ECF1",
                fontSize: "24px",
                fontWeight: "600",
                marginBottom: "32px",
                textAlign: "center",
              }}
            >
              Share your level
            </h2>

            {/* Chat Selection */}
            <div style={{ marginBottom: "24px" }}>
              <div
                className="textfield-1 position-relative"
                ref={chatDropdownRef}
              >
                <input
                  type="text"
                  className="custom-select text-body-b2"
                  placeholder=""
                  value={getSelectedDisplayText()}
                  onClick={() => setIsChatDropdownOpen(!isChatDropdownOpen)}
                  readOnly
                  disabled={loadingFromFirestore}
                  style={{
                    opacity: loadingFromFirestore ? 0.6 : 1,
                  }}
                />
                {isChatDropdownOpen && (
                  <div
                    className="custom-select-dropdown"
                    style={{
                      zIndex: 10,
                      display: "block",
                      position: "absolute",
                      width: "100%",
                      background: "#192f41",
                      border: "1px solid #2d3c48",
                      borderRadius: "20px",
                      padding: "16px 20px",
                      boxShadow: "2px 4px 6px 0px #0000002e",
                      maxHeight: "300px",
                      overflowY: "auto",
                    }}
                  >
                    {/* Search input to look up users (queries Firestore on demand) */}
                    <div style={{ marginBottom: "12px" }}>
                      <input
                        type="text"
                        placeholder="Search people to share... (type 2+ chars)"
                        className="text-body-b2"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          background: "#0f2734",
                          color: "#e6ecf1",
                          border: "1px solid #23323d",
                        }}
                        onKeyDown={(e) => {
                          // optional: submit on Enter
                          if (e.key === "Enter") {
                            searchUsers(searchQuery);
                          }
                        }}
                      />
                    </div>

                    {/* If search query present, show live search results first */}
                    {searchQuery && searchQuery.trim().length >= 2 ? (
                      <div>
                        {searchingUsers ? (
                          <div className="text-body-b2 text-white">
                            Searching...
                          </div>
                        ) : searchResults.length > 0 ? (
                          searchResults.map((u) => (
                            <div
                              key={u.id}
                              className="d-flex align-items-center mb-2"
                              style={{ cursor: "pointer", gap: "12px" }}
                              onClick={async () => {
                                // ensure conversation exists, then select it
                                const convoId =
                                  await ensureDirectConversationWith(u);
                                if (convoId) {
                                  // add to allConversations if missing
                                  const exists = allConversations.find(
                                    (c) => c.conversationId === convoId,
                                  );
                                  if (!exists) {
                                    setAllConversations((prev) => [
                                      {
                                        conversationId: convoId,
                                        name: u.name,
                                        profileImage: u.profileImage || u.image,
                                        type: "direct",
                                        displayText: u.name,
                                      },
                                      ...prev,
                                    ]);
                                  }
                                  // select it
                                  if (!selectedChats.includes(convoId)) {
                                    setSelectedChats((prev) => [
                                      ...prev,
                                      convoId,
                                    ]);
                                    setSelectedConversationIds((prev) => [
                                      ...prev,
                                      convoId,
                                    ]);
                                  }
                                  setSearchQuery("");
                                  setSearchResults([]);
                                  setIsChatDropdownOpen(false);
                                }
                              }}
                            >
                              <div
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  border: "2px solid #2d3c48",
                                  borderRadius: "4px",
                                  flexShrink: 0,
                                }}
                              />
                              <span className="body-1-default label-san-target text-truncate-ellipsis">
                                {u.name}
                              </span>
                            </div>
                          ))
                        ) : (
                          <div className="text-body-b2 text-white">
                            No users found
                          </div>
                        )}
                        <hr
                          style={{ margin: "12px 0", borderColor: "#23323d" }}
                        />
                      </div>
                    ) : null}

                    {/* Individual Chats Section */}
                    {allConversations.filter((conv) => conv.type === "direct")
                      .length > 0 && (
                      <>
                        <h5
                          className="text-body-b2 mb-2"
                          style={{ color: "#E6ECF1", fontWeight: "500" }}
                        >
                          Individual Chat
                        </h5>
                        {allConversations
                          .filter((conv) => conv.type === "direct")
                          .map((conversation) => (
                            <div
                              key={conversation.conversationId}
                              className="d-flex align-items-center mb-2"
                              style={{ cursor: "pointer", gap: "12px" }}
                              onClick={() =>
                                handleChatSelection(conversation.conversationId)
                              }
                            >
                              {/* Custom Square Checkbox */}
                              <div
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  border: selectedChats.includes(
                                    conversation.conversationId,
                                  )
                                    ? "2px solid #00B4FF"
                                    : "2px solid #2d3c48",
                                  borderRadius: "4px",
                                  backgroundColor: selectedChats.includes(
                                    conversation.conversationId,
                                  )
                                    ? "#00B4FF"
                                    : "transparent",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                  flexShrink: 0,
                                }}
                              >
                                {selectedChats.includes(
                                  conversation.conversationId,
                                ) && (
                                  <svg
                                    width="12"
                                    height="9"
                                    viewBox="0 0 12 9"
                                    fill="none"
                                    style={{ color: "white" }}
                                  >
                                    <path
                                      d="M1 4.5L4.5 8L11 1.5"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </div>
                              <span className="body-1-default label-san-target text-truncate-ellipsis">
                                {conversation.displayText}
                              </span>
                            </div>
                          ))}
                      </>
                    )}

                    {/* Separator Line */}
                    {allConversations.filter((conv) => conv.type === "direct")
                      .length > 0 &&
                      allConversations.filter((conv) => conv.type === "group")
                        .length > 0 && (
                        <hr
                          style={{ margin: "16px 0", borderColor: "#2d3c48" }}
                        />
                      )}

                    {/* Group Chats Section */}
                    {allConversations.filter((conv) => conv.type === "group")
                      .length > 0 && (
                      <>
                        <h5
                          className="text-body-b2 mb-2"
                          style={{ color: "#E6ECF1", fontWeight: "500" }}
                        >
                          Group Chat
                        </h5>
                        {allConversations
                          .filter((conv) => conv.type === "group")
                          .map((conversation) => (
                            <div
                              key={conversation.conversationId}
                              className="d-flex align-items-center mb-2"
                              style={{ cursor: "pointer", gap: "12px" }}
                              onClick={() =>
                                handleChatSelection(conversation.conversationId)
                              }
                            >
                              {/* Custom Square Checkbox */}
                              <div
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  border: selectedChats.includes(
                                    conversation.conversationId,
                                  )
                                    ? "2px solid #00B4FF"
                                    : "2px solid #2d3c48",
                                  borderRadius: "4px",
                                  backgroundColor: selectedChats.includes(
                                    conversation.conversationId,
                                  )
                                    ? "#00B4FF"
                                    : "transparent",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                  flexShrink: 0,
                                }}
                              >
                                {selectedChats.includes(
                                  conversation.conversationId,
                                ) && (
                                  <svg
                                    width="12"
                                    height="9"
                                    viewBox="0 0 12 9"
                                    fill="none"
                                    style={{ color: "white" }}
                                  >
                                    <path
                                      d="M1 4.5L4.5 8L11 1.5"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </div>
                              <span className="body-1-default label-san-target text-truncate-ellipsis">
                                {conversation.displayText}
                              </span>
                            </div>
                          ))}
                      </>
                    )}

                    {allConversations.length === 0 && !loadingFromFirestore && (
                      <span className="text-body-b2 text-muted">
                        No conversations found
                      </span>
                    )}
                  </div>
                )}
                <label
                  className="text-body-b2"
                  style={{ background: "#132534" }}
                >
                  Chat
                </label>
              </div>
            </div>

            {/* Tribe Groups Selection */}
            <div style={{ marginBottom: "24px" }}>
              <div
                className="textfield-1 position-relative"
                ref={tribeGroupDropdownRef}
              >
                <input
                  type="text"
                  className="custom-select text-body-b2"
                  placeholder=""
                  value={getSelectedTribeGroupsDisplayText()}
                  onClick={() =>
                    setIsTribeGroupDropdownOpen(!isTribeGroupDropdownOpen)
                  }
                  readOnly
                  disabled={tribeGroupsLoading}
                  style={{
                    opacity: tribeGroupsLoading ? 0.6 : 1,
                  }}
                />
                {isTribeGroupDropdownOpen && (
                  <div
                    className="custom-select-dropdown"
                    style={{
                      zIndex: 10,
                      display: "block",
                      position: "absolute",
                      width: "100%",
                      background: "#192f41",
                      border: "1px solid #2d3c48",
                      borderRadius: "20px",
                      padding: "16px 20px",
                      boxShadow: "2px 4px 6px 0px #0000002e",
                      maxHeight: "300px",
                      overflowY: "auto",
                    }}
                  >
                    <h5
                      className="text-body-b2 mb-2"
                      style={{ color: "#E6ECF1", fontWeight: "500" }}
                    >
                      Tribe Groups
                    </h5>
                    {tribeGroups.map((group) => (
                      <div
                        key={group.id}
                        className="d-flex align-items-center mb-2"
                        style={{ cursor: "pointer", gap: "12px" }}
                        onClick={() => handleTribeGroupSelection(group.id)}
                      >
                        {/* Custom Square Checkbox */}
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            border: selectedTribeGroupIds.includes(group.id)
                              ? "2px solid #00B4FF"
                              : "2px solid #2d3c48",
                            borderRadius: "4px",
                            backgroundColor: selectedTribeGroupIds.includes(
                              group.id,
                            )
                              ? "#00B4FF"
                              : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.2s ease",
                            flexShrink: 0,
                          }}
                        >
                          {selectedTribeGroupIds.includes(group.id) && (
                            <svg
                              width="12"
                              height="9"
                              viewBox="0 0 12 9"
                              fill="none"
                              style={{ color: "white" }}
                            >
                              <path
                                d="M1 4.5L4.5 8L11 1.5"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>
                        <span className="body-1-default label-san-target text-truncate-ellipsis">
                          {group.name}
                        </span>
                      </div>
                    ))}

                    {tribeGroups.length === 0 && !tribeGroupsLoading && (
                      <span className="text-body-b2 text-muted">
                        No tribe groups found
                      </span>
                    )}
                  </div>
                )}
                <label
                  className="text-body-b2"
                  style={{ background: "#132534" }}
                >
                  Tribe Groups
                </label>
              </div>
            </div>

            {/* Sharable Toggle */}
            {/* <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '24px'
                        }}>
                            <span style={{
                                color: '#E6ECF1',
                                fontSize: '16px',
                                fontWeight: '500'
                            }}>
                                Sharable
                            </span>
                            <label style={{
                                position: 'relative',
                                display: 'inline-block',
                                width: '60px',
                                height: '34px'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={isSharable}
                                    onChange={(e) => setIsSharable(e.target.checked)}
                                    style={{ opacity: 0, width: 0, height: 0 }}
                                />
                                <span style={{
                                    position: 'absolute',
                                    cursor: 'pointer',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: isSharable ? '#00B4FF' : '#ccc',
                                    transition: '0.4s',
                                    borderRadius: '34px'
                                }}>
                                    <span style={{
                                        position: 'absolute',
                                        content: '',
                                        height: '26px',
                                        width: '26px',
                                        left: isSharable ? '30px' : '4px',
                                        bottom: '4px',
                                        backgroundColor: 'white',
                                        transition: '0.4s',
                                        borderRadius: '50%'
                                    }} />
                                </span>
                            </label>
                        </div> */}

            {/* Description */}
            <div style={{ marginBottom: "32px" }}>
              <div className="notes-text-editor for-task-management">
                <label
                  className="text-small-s2 px-1"
                  style={{ color: "#d0d8df" }}
                >
                  Description
                </label>
                <MyEditor
                  value={description}
                  onChange={handleDescriptionChange}
                />
              </div>
            </div>
          </div>

          {/* Share Button */}
          <div className="d-flex gap-3 mt-4 justify-content-center">
            <button
              className="btn-1 btn-1-primary px-3 text-body-b1"
              style={{ width: "182px" }}
              onClick={handleShareToChat}
              disabled={
                isSharing ||
                isPostingToTribe ||
                (selectedConversationIds.length === 0 &&
                  selectedTribeGroupIds.length === 0)
              }
            >
              {isSharing || isPostingToTribe ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : (
                "Share"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShareLevelupModal;
